import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, AlertCircle, Home, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { getAuthReturnTo, normalizeAuthReturnTo, readExplicitReturnTo, withReturnTo } from '@/routing/authReturnTo';
import { AUTH_RECOVERY_COPY } from '@/lib/authRecovery';
import { track, analyticsEvents } from '@/lib/analytics/posthog';
import { landingContext } from '@/lib/personas/intent';
import { EMAIL_CHANGE_RETURN_TO, EMAIL_CHANGE_TYPE } from '@/lib/emailChange';
import { isMissingVerifier } from '@/lib/recoveryLink';

const EMAIL_CHANGE_EXPIRED = 'That link expired or was already used. Ask for a new one from Edit profile.';
const EMAIL_CHANGE_OTHER_BROWSER =
  'Open the link in the browser where you asked for the change. If you already opened it, sign in with your new address.';
// Supabase confirms the address before it redirects here, but the PKCE verifier lives in the
// browser that signed up. A link opened anywhere else (a mail app's browser, another device)
// cannot start a session, so the account is ready and the visitor only needs to sign in.
// OAuth returns land here too and can lose their verifier, so the copy must not promise a
// confirmed email to someone who signed in with Google.
const LINK_OTHER_BROWSER =
  "This link opened in a different browser than the one you started in. If you were confirming your email, it's confirmed. Sign in here to continue.";
// Mail scanners often open links before the visitor does, which spends the token.
const LINK_EXPIRED =
  'That link expired or was already used. Try signing in. If your email still needs confirming, you can send a new link from there.';

interface CallbackState {
  loading: boolean;
  error: string | null;
  success: boolean;
  // Set when the account may be fine and signing in is the next step.
  signInHref?: string;
}

export const AuthCallback = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const startedRef = useRef(false);
  const [state, setState] = useState<CallbackState>({
    loading: true,
    error: null,
    success: false
  });
  // The timeout below is created once, so it reads loading through a ref; reading state there
  // would see the first render's loading: true and replace a shown error after 10 seconds.
  const loadingRef = useRef(true);
  useEffect(() => {
    loadingRef.current = state.loading;
  }, [state.loading]);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get parameters from both hash and query string
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);
        
        // Check for OAuth errors first (in query params or hash)
        const error = queryParams.get('error') || hashParams.get('error');
        const errorDescription = queryParams.get('error_description') || hashParams.get('error_description');
        
        // Confirmation links for a sign-in email change carry type=email_change in the
        // query (our redirect) or the hash (Supabase's implicit redirect and templates).
        const isEmailChange = (queryParams.get('type') ?? hashParams.get('type')) === EMAIL_CHANGE_TYPE;

        const promptSignIn = (message: string, reason: 'otp_expired' | 'other_browser') => {
          const storedReturnTo = sessionStorage.getItem('auth_redirect');
          const returnTo = readExplicitReturnTo(queryParams)
            ?? (storedReturnTo ? normalizeAuthReturnTo(storedReturnTo) : null);
          track(analyticsEvents.AUTH_CALLBACK_FAILED, { reason });
          setState({
            loading: false,
            error: message,
            success: false,
            signInHref: withReturnTo('/auth', returnTo),
          });
          toast.info('Sign in to continue', { description: message, duration: 6000 });
        };

        if (error && isEmailChange) {
          setState({ loading: false, error: EMAIL_CHANGE_EXPIRED, success: false });
          toast.error('Email not changed', { description: EMAIL_CHANGE_EXPIRED, duration: 5000 });
          return;
        }

        const errorCode = queryParams.get('error_code') || hashParams.get('error_code');
        if (errorCode === 'otp_expired') {
          promptSignIn(LINK_EXPIRED, 'otp_expired');
          return;
        }

        if (error) {
          console.log('OAuth callback received an error');
          
          // Handle different error types
          if (error === 'access_denied') {
            setState({ loading: false, error: null, success: false });
            toast.info('Login cancelled', {
              description: 'You cancelled the login process. No worries!',
              duration: 4000
            });
            
            // Auto-redirect to home after showing message
            setTimeout(() => {
              navigate('/', { replace: true });
            }, 3000);
            
            return;
          } else {
            // Handle other OAuth errors
            console.error('OAuth callback failed');
            track(analyticsEvents.AUTH_CALLBACK_FAILED, { reason: 'oauth_error' });
            setState({ 
              loading: false, 
              error: AUTH_RECOVERY_COPY.oauth,
              success: false 
            });
            
            toast.error('Authentication Error', {
              description: AUTH_RECOVERY_COPY.oauth,
              duration: 5000
            });
            
            return;
          }
        }
        
        // Handle successful OAuth callback (PKCE returns a `code`; implicit returns `access_token`)
        const accessToken = hashParams.get('access_token');
        const authCode = queryParams.get('code') || hashParams.get('code');
        const persistedRedirect = sessionStorage.getItem('auth_redirect');
        const getRedirectTarget = () => getAuthReturnTo(
          queryParams,
          normalizeAuthReturnTo(persistedRedirect),
        );

        // Production confirms signups through Supabase's own email link and
        // lands back here; the dev verify route redirects back with
        // ?verified=true. Both complete the signup funnel in this component,
        // so the capture lives here — email_confirmed_at is set strictly
        // after created_at only when a confirmation link (not auto-confirm)
        // verified the address.
        const signupVerificationProps = (
          session: { user?: { created_at?: string | null; email_confirmed_at?: string | null } | null } | null | undefined,
        ): Record<string, number> | null => {
          if (queryParams.get('verified') !== 'true') {
            const created = session?.user?.created_at ? Date.parse(session.user.created_at) : NaN;
            const confirmed = session?.user?.email_confirmed_at ? Date.parse(session.user.email_confirmed_at) : NaN;
            if (!Number.isFinite(created) || !Number.isFinite(confirmed) || confirmed <= created) return null;
            return { verification_delay_hours: Math.round(((confirmed - created) / 36e5) * 10) / 10 };
          }
          return {};
        };

        const completeSignIn = (
          delayMs: number,
          method: string,
          session?: { user?: { created_at?: string | null; email_confirmed_at?: string | null } | null } | null,
        ) => {
          const verification = signupVerificationProps(session);
          if (verification) track(analyticsEvents.SIGNUP_EMAIL_VERIFIED, verification);
          track(analyticsEvents.SIGNED_IN, { method, ...landingContext() });
          setState({ loading: false, error: null, success: true });
          const redirectTo = getRedirectTarget();
          window.history.replaceState(null, '', window.location.pathname);
          sessionStorage.removeItem('auth_redirect');
          toast.success('Successfully signed in!', { description: 'Welcome back to Light Hunt', duration: 3000 });
          setTimeout(() => navigate(redirectTo, { replace: true }), delayMs);
        };

        if (isEmailChange) {
          // Supabase already applied the change when it verified the link. Here we only
          // pick up the session and tell the user where things stand.
          let user: { new_email?: string | null } | null | undefined;
          const tokenHash = queryParams.get('token_hash');
          try {
            if (tokenHash) {
              const { data, error: verifyError } = await supabase.auth.verifyOtp({ type: EMAIL_CHANGE_TYPE, token_hash: tokenHash });
              if (verifyError) throw verifyError;
              user = data.session?.user ?? data.user;
            } else if (authCode) {
              const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(authCode);
              if (exchangeError) throw exchangeError;
              user = data.session?.user;
            } else if (accessToken) {
              const { data, error: sessionError } = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: hashParams.get('refresh_token') || '',
              });
              if (sessionError) throw sessionError;
              user = data.session?.user;
            }
            // No credentials: the first of two links was accepted and Supabase sent only a message.
          } catch (linkError) {
            const copy = isMissingVerifier(linkError as { name?: string; message?: string })
              ? EMAIL_CHANGE_OTHER_BROWSER
              : EMAIL_CHANGE_EXPIRED;
            setState({ loading: false, error: copy, success: false });
            toast.error('Email not changed', { description: copy, duration: 5000 });
            return;
          }

          const waitingOnOtherLink = !user || Boolean(user.new_email);
          if (waitingOnOtherLink) {
            toast.info('One more link to open', {
              description: 'Open the link we sent to your other address to finish the change.',
              duration: 6000,
            });
          } else {
            toast.success('Sign-in email changed', {
              description: 'Use your new address the next time you sign in.',
              duration: 5000,
            });
          }
          setState({ loading: false, error: null, success: true });
          window.history.replaceState(null, '', window.location.pathname);
          navigate(EMAIL_CHANGE_RETURN_TO, { replace: true });
          return;
        }

        // Redeem link credentials before looking at any existing session. The client runs with
        // detectSessionInUrl: false, so nothing else redeems them, and a session already in
        // storage may belong to a different account than the one this link signs in.
        if (authCode) {
          const { data: { session }, error: exchangeError } = await supabase.auth.exchangeCodeForSession(authCode);
          if (exchangeError && isMissingVerifier(exchangeError)) {
            promptSignIn(LINK_OTHER_BROWSER, 'other_browser');
            return;
          }
          if (exchangeError) throw exchangeError;
          if (!session) throw new Error('Code exchange returned no session');
          completeSignIn(1000, session.user?.app_metadata?.provider ?? 'oauth', session);
          return;
        }

        if (accessToken) {
          const { data: { session }, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: hashParams.get('refresh_token') || '',
          });
          if (sessionError) throw sessionError;
          if (!session) throw new Error('setSession returned no session');
          completeSignIn(1000, session.user?.app_metadata?.provider ?? 'oauth', session);
          return;
        }

        // No credentials in the URL: an existing session is the only thing to honor.
        const existingSession = await supabase.auth.getSession();
        if (existingSession.data.session) {
          completeSignIn(500, existingSession.data.session.user?.app_metadata?.provider ?? 'existing_session', existingSession.data.session);
          return;
        }

        throw new Error('No access token or authorization code received');
      } catch (error) {
        console.error('Auth callback failed');
        track(analyticsEvents.AUTH_CALLBACK_FAILED, { reason: 'failed' });
        setState({ 
          loading: false, 
          error: 'Something went wrong during authentication', 
          success: false 
        });
        
        toast.error('Authentication Error', {
          description: 'Something went wrong. Please try logging in again.',
          duration: 5000
        });
      }
    };

    // Set up timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (loadingRef.current) {
        console.warn('Auth callback timeout - redirecting to home');
        setState({ 
          loading: false, 
          error: 'Authentication timeout', 
          success: false 
        });
        
        toast.error('Timeout', {
          description: 'Authentication took too long. Please try again.',
          duration: 4000
        });
        
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 2000);
      }
    }, 10000); // 10 second timeout

    // Once started, ignore re-runs: redeeming a link flips isAuthenticated, and re-running
    // would navigate home over the pending returnTo redirect.
    if (startedRef.current) return () => clearTimeout(timeoutId);
    startedRef.current = true;

    // A link credential is always redeemed, even when someone is already signed in.
    const urlParams = new URLSearchParams(window.location.search);
    const urlHash = new URLSearchParams(window.location.hash.substring(1));
    const hasLinkCredential = ['code', 'access_token', 'error', 'token_hash'].some(
      (key) => urlParams.has(key) || urlHash.has(key),
    ) || (urlParams.get('type') ?? urlHash.get('type')) === EMAIL_CHANGE_TYPE;

    if (hasLinkCredential || (!isAuthenticated && (window.location.hash || window.location.search))) {
      handleCallback();
    } else {
      clearTimeout(timeoutId);
      navigate('/', { replace: true });
    }

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, navigate]);

  // Loading state
  if (state.loading) {
    return (
      <article
        aria-label="Authentication handoff"
        aria-describedby="auth-callback-description"
        className="auth-callback-page min-h-screen flex items-center justify-center"
      >
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <h1 id="auth-callback-title" className="text-2xl font-semibold mb-2">Completing sign in...</h1>
          <p id="auth-callback-description" className="text-muted-foreground">Please wait while we redirect you.</p>
        </div>
      </article>
    );
  }

  // Success state
  if (state.success) {
    return (
      <article
        aria-label="Authentication handoff"
        aria-describedby="auth-callback-description"
        className="auth-callback-page min-h-screen flex items-center justify-center"
      >
        <div className="text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 id="auth-callback-title" className="text-2xl font-semibold mb-2 text-green-600">Success!</h1>
          <p id="auth-callback-description" className="text-muted-foreground">Redirecting you now...</p>
        </div>
      </article>
    );
  }

  // Error state
  return (
    <article
      aria-label="Authentication handoff"
      aria-describedby="auth-callback-description"
      className="auth-callback-page min-h-screen flex items-center justify-center"
    >
      <div className="text-center max-w-md px-4">
        <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-4" />
        <h1 id="auth-callback-title" className="text-2xl font-semibold mb-2">
          {state.signInHref ? 'Sign in to continue' : 'Authentication Issue'}
        </h1>
        {state.error ? (
          <p id="auth-callback-description" className="text-muted-foreground mb-6">{state.error}</p>
        ) : (
          <p id="auth-callback-description" className="text-muted-foreground mb-6">
            Your login was cancelled or interrupted. You can try again or return to the homepage.
          </p>
        )}
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button 
            onClick={() => navigate('/', { replace: true })}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Home className="w-4 h-4" />
            Go Home
          </Button>
          <Button 
            onClick={() => navigate(state.signInHref ?? '/auth', { replace: true })}
            className="flex items-center gap-2"
          >
            <LogIn className="w-4 h-4" />
            {state.signInHref ? 'Sign in' : 'Try Again'}
          </Button>
        </div>
        </div>
    </article>
  );
};
